import { Select, Spin, Tag } from "antd";
import type { SelectProps } from "antd/es/select";
import { getSkillSearchResult } from "api/search.api";
import { SvgIcons } from "Icon/SvgIcons";

import debounce from "lodash/debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";

type TagRender = SelectProps['tagRender'];

export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, "options" | "children"> {
  fetchOptions: (search: string) => Promise<ValueType[]>;
  debounceTimeout?: number;
  handleDeselect: any;
}

const createTagRender = (handleDeselect?: any): TagRender => (props) => {
  const { label, value, closable, onClose } = props;
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClose = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (onClose) {
      onClose(); // Perform the default close action
      if (value && typeof value === "string") {
        handleDeselect?.(value); // Call handleDeselect with the tag value
      }
    }
  };

  return (
    <Tag
      color="#F9F5FF"
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={handleClose} // Updated to use handleClose
      bordered={true}
      closeIcon={SvgIcons.closeIcon}
      style={{
        marginInlineEnd: 4,
        border: "1px solid #7F56D9",
        color: "#344054",
        display: "flex",
        alignItems: "center",
        textTransform: "capitalize",
        margin: "2.5px",
        fontWeight: 500,
        padding: "2px 4px",
      }}
    >
      {label}
    </Tag>
  );
};

function DebounceSelect<
  ValueType extends {
    key?: string;
    label: React.ReactNode;
    value: string | number;
  } = any
>({ fetchOptions, debounceTimeout = 800, handleDeselect, ...props }: DebounceSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const fetchRef = useRef(0);
  const [open, setOpen] = useState(false);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // Ignore late fetches
          return;
        }
        setOpen(true);
        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <Select
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      onDeselect={handleDeselect} 
      placeholder="eg: React.js"
      notFoundContent={fetching ? <Spin size="small" /> : null}
      getPopupContainer={(triggerNode) => triggerNode.parentElement}
      options={options}
      {...props}
      open={open}
      onDropdownVisibleChange={(visible) => {
        if (!visible) {
          setOptions([]);
          setFetching(false);
        }
        setOpen(visible);
      }}
      style={{
        borderRadius: "8px",
        width: "100%",
        // height: "40px",
        fontWeight: "normal",
      }}
      tagRender={createTagRender(handleDeselect)} // Updated to pass handleDeselect
      onSelect={() => setOpen(false)}
      // onBlur={onBlur}
    />
  );
}

// Usage of DebounceSelect
interface UserValue {
  label: string;
  value: string;
}

export async function fetchUserList(username: string): Promise<UserValue[]> {
  return getSkillSearchResult(username).then((data: any) => {
    return data?.data?.autocomplete?.result?.map((label: any) => ({
      label: label,
      value: label,
    }));
  });
}

interface CompanyDropdownProps {
  className?: string;
  placeholder?: string;
  onClick?: any;
  onChange?: any;
  defaultValue?: any;
  valueSetterRequired?: any;
  [key: string]: any;
  handleDeselect: (value: string) => void;
}
const MultiSkillSelector: React.FC<CompanyDropdownProps> = ({
  className,
  handleDeselect,
  placeholder,
  onClick,
  defaultValue,
  valueSetterRequired,
  onChange,
  ...props
}) => {
  const [value, setValue] = useState<UserValue[]>(defaultValue ? defaultValue : []);
  
  useEffect(()=>{
    if (defaultValue) {
      setValue(defaultValue);
    }
  }, [defaultValue]);
  
  valueSetterRequired =
    valueSetterRequired == true || valueSetterRequired == false ? valueSetterRequired : true;

  return (
    <DebounceSelect
      mode="tags"
      // maxTagCount="responsive"
      handleDeselect={handleDeselect}
      value={value.length > 0 ? value : undefined}
      placeholder={placeholder}
      fetchOptions={fetchUserList}
      onChange={(newValue) => {
        onChange && onChange(newValue);
        setValue(newValue as UserValue[]);
      }}
      onClick={onClick}
      style={{ width: "100%" }}
      className={className}
      {...props}
    />
  );
};

export default MultiSkillSelector;
